/**
 * Storefront redirect pop-up.
 *
 * ILVA runs Denmark and Sweden as two SEPARATE Shopify stores, so Shopify's
 * own market redirects and the Geolocation app — which only move a visitor
 * between markets of one store — cannot offer the other website. This asks
 * instead: a visitor whose IP country matches the other shop is offered a
 * jump to it, and always chooses. Nobody is redirected automatically (GeoIP
 * is wrong behind VPNs and Private Relay, and crawlers browse from the US).
 *
 * The country comes from Shopify's own browsing-context endpoint. Its
 * `detected_values.country.handle` is the visitor's IP country and is
 * returned whether or not the store sells to that country — only the
 * `suggestions` array is limited to the store's markets, which is why this
 * works on single-market stores. The response is never CDN-cached, so the
 * country is always the caller's; we cache it per session ourselves.
 *
 * Scoped in an IIFE so its cookie helpers do not collide with the identically
 * named globals in pop-up.js and age-verification-pop-up.js.
 */

/* global Modal, Shopify, theme */

(() => {
  const COUNTRY_CACHE_KEY = 'storefront-redirect-country';

  /**
   * Sets a cookie.
   * @param {string} name - Name for the cookie.
   * @param {string} value - Value for the cookie.
   * @param {number} days - Number of days until the cookie should expire.
   */
  function setCookie(name, value, days) {
    let expires = '';

    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
      expires = `; expires=${date.toUTCString()}`;
    }

    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}${expires}; path=/; SameSite=None; Secure`;
  }

  /**
   * Gets the value of a cookie (if it exists).
   * @param {string} name - Name of the cookie.
   * @returns {?string}
   */
  function getCookie(name) {
    const cookieString = `; ${document.cookie}`;
    const cookies = cookieString.split(`; ${name}=`);

    if (cookies.length === 2) {
      return cookies.pop().split(';').shift();
    }

    return null;
  }

  /**
   * Strips a leading 'www.' so hosts compare like for like.
   * @param {string} host - Host name.
   * @returns {string}
   */
  function bareHost(host) {
    return host.toLowerCase().replace(/^www\./, '');
  }

  /**
   * Asks Shopify for the visitor's country, once per session.
   * @returns {Promise<?string>} Two-letter country code, or null if unknown.
   */
  async function detectCountry() {
    const cached = theme.storageUtil.get(COUNTRY_CACHE_KEY, false, true);
    if (cached) return cached;

    // Shopify.routes.root keeps the request on the current locale (e.g. /sv/).
    const root = Shopify?.routes?.root || '/';
    const response = await fetch(`${root}browsing_context_suggestions.json?country[enabled]=true`);
    if (!response.ok) return null;

    const data = await response.json();
    const country = data?.detected_values?.country?.handle || null;
    if (country) theme.storageUtil.set(COUNTRY_CACHE_KEY, country, true);

    return country;
  }

  if (!customElements.get('storefront-redirect')) {
    customElements.whenDefined('modal-dialog').then(() => {
      class StorefrontRedirect extends Modal {
        /**
         * Triggers when the web component is added to the DOM. Attributes are
         * only guaranteed to be readable here, not in the constructor.
         */
        connectedCallback() {
          if (this.initialised) return;
          this.initialised = true;

          this.cookie = `${this.id}-dismissed`;
          this.dismissDays = Number(this.dataset.dismissDays);

          if (Shopify.designMode) {
            this.selectHandler = (evt) => {
              if (evt.target === this.closest('.shopify-section')) this.open();
            };
            this.deselectHandler = this.close.bind(this);
            document.addEventListener('shopify:section:select', this.selectHandler);
            document.addEventListener('shopify:section:deselect', this.deselectHandler);
            return;
          }

          this.setupGoLinkHandler();
          this.maybeShow();
        }

        /**
         * Attaches a click handler to the Go link so it sets the dismiss cookie
         * before navigating to the other shop. Without this, a Back navigation
         * would show the pop-up again, since no answer was recorded.
         */
        setupGoLinkHandler() {
          const goLink = this.querySelector('a.btn--primary');
          if (!goLink) return;

          goLink.addEventListener('click', () => {
            this.close();
          });
        }

        /**
         * Triggers when the web component is removed from the DOM.
         */
        disconnectedCallback() {
          clearTimeout(this.openTimeout);

          if (this.selectHandler) {
            document.removeEventListener('shopify:section:select', this.selectHandler);
            document.removeEventListener('shopify:section:deselect', this.deselectHandler);
          }
        }

        /**
         * Opens the pop-up if this visitor belongs on the other website and
         * has not already answered the question.
         */
        async maybeShow() {
          if (getCookie(this.cookie)) return;

          // A deliberate move between the shops is never second-guessed: the
          // answer is remembered so the other shop doesn't ask on arrival.
          if (this.arrivedFromOtherShop()) {
            setCookie(this.cookie, true, this.dismissDays);
            return;
          }

          const targetCountry = (this.dataset.targetCountry || '').toUpperCase();
          if (!targetCountry) return;

          const country = await this.resolveCountry();
          if (!country || country.toUpperCase() !== targetCountry) return;

          this.openTimeout = setTimeout(() => this.open(), Number(this.dataset.delay) * 1000);
        }

        /**
         * Whether the visitor reached this page from the other ILVA website,
         * either by the country selector or by this pop-up's own button.
         *
         * The flag arrives as a '#shop_switch' fragment, which search engines
         * ignore, so the cross-domain link stays crawlable as a clean address.
         * A '?shop_switch=1' parameter is accepted too, for a hand-built link.
         * @returns {boolean}
         */
        arrivedFromOtherShop() {
          if (window.location.hash === '#shop_switch') {
            this.clearSwitchFragment();
            return true;
          }

          if (new URLSearchParams(window.location.search).get('shop_switch') === '1') return true;

          const otherHost = this.dataset.otherHost;
          if (!otherHost || !document.referrer) return false;

          try {
            return bareHost(new URL(document.referrer).hostname) === bareHost(otherHost);
          } catch (error) {
            return false;
          }
        }

        /**
         * Drops our own fragment from the address bar once it has been read, so
         * the visitor does not see it and cannot copy it into a shared link.
         */
        clearSwitchFragment() {
          try {
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
          } catch (error) {
            /* Safari rate-limits repeated history calls — the flag is already read,
               so a tidier address bar is all that is lost. */
          }
        }

        /**
         * The visitor's country. '?redirect_country=SE' forces a value for
         * testing, since a developer's own IP only ever resolves to one place.
         * @returns {Promise<?string>}
         */
        resolveCountry() {
          const override = new URLSearchParams(window.location.search).get('redirect_country');
          if (override) return Promise.resolve(override);

          // A failed lookup means no pop-up, never a guess.
          return detectCountry().catch(() => null);
        }

        /**
         * Handles 'close' events on the modal.
         */
        close() {
          super.close();
          setCookie(this.cookie, true, this.dismissDays);
        }
      }

      customElements.define('storefront-redirect', StorefrontRedirect);
    });
  }
})();
